import { mapValues } from 'lodash/fp'

import { getPxInRem } from './styles'

// consistent spacing values (in rem or em)
const spaceValues = {
  none: `0`,
  extraSmall: `0.25`,
  small: `0.5`,
  smallMedium: `0.75`,
  medium: `1`,
  mediumLarge: `1.25`,
  large: `1.5`,
  largePlus: `2`,
  extraLarge: `3`,
  extraLargePlus: `4`,
  mega: `5`,
} as const

// values above transformed by adding the units (rem or em)
export const spaces = {
  ...(mapValues((space) => `${space}rem`)(spaceValues) as typeof spaceValues),
  em: mapValues((space) => `${space}em`)(spaceValues) as typeof spaceValues,
}

// define one space for all directions
export type TSingleSpace = keyof typeof spaceValues
export type TSingleMargin = TSingleSpace | 'auto'
// define spaces for X and Y axes
export type TDoubleSpace = {
  horizontal: TSingleSpace
  vertical: TSingleSpace
}
export type TDoubleMargin = {
  horizontal: TSingleMargin
  vertical: TSingleMargin
}
// define spaces for each direction separately
export type TMultiSpace = {
  top: TSingleSpace
  bottom: TSingleSpace
  left: TSingleSpace
  right: TSingleSpace
}
export type TMultiMargin = {
  top: TSingleMargin
  bottom: TSingleMargin
  left: TSingleMargin
  right: TSingleMargin
}
// mostly use this as a prop – allows all options above
export type TSpace = TSingleSpace | TDoubleSpace | TMultiSpace
export type TMargin = TSingleMargin | TDoubleMargin | TMultiMargin

const getSingleSpaceValue = (space: TSingleSpace | TSingleMargin, useEm: boolean = false) => {
  if (space === 'auto') {
    return space
  }

  const spaceObject = useEm ? spaces.em : spaces

  return spaceObject[space]
}

// transform space (prop) key(s) to the values used as padding / margin / ...
export const getSpacingValue = (space: TSpace | TMargin, useEm: boolean = false) => {
  if (typeof space === 'string') {
    return getSingleSpaceValue(space, useEm)
  }

  if ('horizontal' in space) {
    return `${getSingleSpaceValue(space.vertical, useEm)} ${getSingleSpaceValue(
      space.horizontal,
      useEm,
    )}`
  }

  if ('top' in space) {
    return `
      ${getSingleSpaceValue(space.top, useEm)} ${getSingleSpaceValue(space.right, useEm)}${``}
      ${getSingleSpaceValue(space.bottom, useEm)} ${getSingleSpaceValue(space.left, useEm)}
      `
  }

  return `initial`
}

// transform space (prop) key(s) to the values used as padding / margin / ...
export const getTopSpacingValue = (space: TSpace | TMargin, useEm: boolean = false) => {
  if (typeof space === 'string') {
    return getSingleSpaceValue(space, useEm)
  }

  if ('horizontal' in space) {
    return getSingleSpaceValue(space.vertical, useEm)
  }

  if ('top' in space) {
    return getSingleSpaceValue(space.top, useEm)
  }

  return `initial`
}

// transform space (prop) key(s) to the values used as padding / margin / ...
export const getBottomSpacingValue = (space: TSpace | TMargin, useEm: boolean = false) => {
  if (typeof space === 'string') {
    return getSingleSpaceValue(space, useEm)
  }

  if ('horizontal' in space) {
    return getSingleSpaceValue(space.vertical, useEm)
  }

  if ('top' in space) {
    return getSingleSpaceValue(space.bottom, useEm)
  }

  return `initial`
}

// transform space (prop) key(s) to the values used as padding / margin / ...
export const getLeftSpacingValue = (space: TSpace | TMargin, useEm: boolean = false) => {
  if (typeof space === 'string') {
    return getSingleSpaceValue(space, useEm)
  }

  if ('horizontal' in space) {
    return getSingleSpaceValue(space.horizontal, useEm)
  }

  if ('top' in space) {
    return getSingleSpaceValue(space.left, useEm)
  }

  return `initial`
}

// transform space (prop) key(s) to the values used as padding / margin / ...
export const getRightSpacingValue = (space: TSpace | TMargin, useEm: boolean = false) => {
  if (typeof space === 'string') {
    return getSingleSpaceValue(space, useEm)
  }

  if ('horizontal' in space) {
    return getSingleSpaceValue(space.horizontal, useEm)
  }

  if ('top' in space) {
    return getSingleSpaceValue(space.right, useEm)
  }

  return `initial`
}

export const getSpacingValueInPx = (spaceString: TSingleSpace | TSingleMargin) => {
  if (spaceString === 'auto') {
    return 0
  }
  const spacingValueInRems = Number(spaceValues[spaceString])
  const pxInRem = getPxInRem()

  return spacingValueInRems * pxInRem
}

type TSpaceOrMargin = TSpace | TMargin
type TGetSpacingKey = (space: TSpaceOrMargin) => TSingleSpace | TSingleMargin

// get top space (prop) key of padding / margin / ...
export const getTopSpacing: TGetSpacingKey = (space) => {
  if (typeof space === 'string') {
    return space
  }

  if ('horizontal' in space) {
    return space.vertical
  }

  if ('top' in space) {
    return space.top
  }

  return `none`
}
// get bottom space (prop) key of padding / margin / ...
export const getBottomSpacing: TGetSpacingKey = (space) => {
  if (typeof space === 'string') {
    return space
  }

  if ('horizontal' in space) {
    return space.vertical
  }

  if ('top' in space) {
    return space.bottom
  }

  return `none`
}
// get left space (prop) key of padding / margin / ...
export const getLeftSpacing: TGetSpacingKey = (space) => {
  if (typeof space === 'string') {
    return space
  }

  if ('horizontal' in space) {
    return space.horizontal
  }

  if ('top' in space) {
    return space.left
  }

  return `none`
}
// get right space (prop) key of padding / margin / ...
export const getRightSpacing: TGetSpacingKey = (space) => {
  if (typeof space === 'string') {
    return space
  }

  if ('horizontal' in space) {
    return space.horizontal
  }

  if ('top' in space) {
    return space.right
  }

  return `none`
}

/**
 * removes value `auto` that is used only as a margin value
 *
 * accepts the `getSpacingKey` function and it can be used as a factory function
 * to produce a function that returns a specified single padding value
 *
 * @example export const getTopPadding = getPaddingOnly(getTopSpacing)
 *
 * const paddingTop = getTopPadding(padding)
 * // paddingTop: "none" | "extraSmall" | "small" | "smallMedium" | "medium" | "large" | "largePlus" | "extraLarge"
 */
const getPaddingOnly =
  (getSpacingKey: TGetSpacingKey) =>
  (space: TSpaceOrMargin): TSingleSpace => {
    const singleSpace = getSpacingKey(space)

    return singleSpace === `auto` ? `none` : singleSpace
  }

/**
 * get top space (prop) key of padding
 */
export const getTopPadding = getPaddingOnly(getTopSpacing)

/**
 * get bottom space (prop) key of padding
 */
export const getBottomPadding = getPaddingOnly(getBottomSpacing)

/**
 * get left space (prop) key of padding
 */
export const getLeftPadding = getPaddingOnly(getLeftSpacing)

/**
 * get right space (prop) key of padding
 */
export const getRightPadding = getPaddingOnly(getRightSpacing)
